import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef } from "react";
import Icon from "../Icon";
import { renderError } from "../../helpers/errors";
import api from "../../services/api";

// Mínimo de caracteres para disparar a busca textual. Exportado porque a tela
// que consome este componente precisa da mesma regra para explicar ao usuário
// por que ainda não há resultados.
export const MIN_SEARCH_LENGTH = 3;

export const SearchProducts = forwardRef(({ search, setSearch, selectedProduct, similarProduct, setSimilarProduct, changeProducts, filterOptions, filters, perPage = 8, enabled = true, page, setCurrentPage, setLoading, setNeedsMoreChars, onlyQuotable = false }, ref) => {
    const [products, setProducts] = useState([]);

    const prevSearch = useRef('');

    // Um AbortController por instância: antes era `window.requestController`,
    // global e compartilhado com SelectProduct/SelectCompany/
    // SelectCustomerCompany — dois campos de busca na mesma tela cancelavam a
    // requisição um do outro.
    const abortRef = useRef(null);

    // Chave estável dos filtros do trilho lateral, para efeitos/deps.
    const filtersKey = JSON.stringify(filters || {});
    const hasActiveFilters = Boolean(filters && Object.keys(filters).length > 0);

    const inputRef = useRef();

    useImperativeHandle(ref, () => ({
        focus: () => {
        inputRef.current?.focus();
        },
        get input() {
        return inputRef.current;
        }
    }));

    useEffect(() => {
        if (search !== prevSearch.current && search.length >= MIN_SEARCH_LENGTH) {
            setCurrentPage?.(1);
        }
        prevSearch.current = search;
    }, [search]);

    useEffect(() => {
        setCurrentPage?.(1);
    }, [similarProduct])

    // Voltar à primeira página quando os filtros do trilho mudam.
    useEffect(() => {
        setCurrentPage?.(1);
    }, [filtersKey]);

    useEffect(() => {
        // Enquanto habilitado (modal aberto), busca quando: o termo tem 3+
        // caracteres, há busca por similaridade, há filtro ativo OU o termo
        // está vazio — neste último caso lista todos os produtos (navegação
        // livre por filtros, sem precisar digitar). Termos de 1-2 caracteres
        // não disparam (evita ruído do Meilisearch).
        const shouldSearch = enabled && (
            similarProduct ||
            hasActiveFilters ||
            search.length === 0 ||
            search.length >= MIN_SEARCH_LENGTH
        );
        let timeout;

        if (!shouldSearch) {
            // Termo curto demais: nada a buscar, mas também não é "nenhum
            // resultado" — a tela precisa saber a diferença para orientar a
            // digitar em vez de dizer que não achou nada.
            abortRef.current?.abort();
            abortRef.current = null;
            setProducts([]);
            setNeedsMoreChars?.(enabled);
            setLoading(false);
            return;
        }

        setNeedsMoreChars?.(false);
        setLoading(true);

        timeout = setTimeout(() => {
            getProducts(search);
        }, 500);

        return () => clearTimeout(timeout);
    }, [search, page, filterOptions?.orientation, similarProduct, filtersKey, enabled]);

    // Requisição em voo não sobrevive ao fechamento do modal.
    useEffect(() => () => abortRef.current?.abort(), []);

    useEffect(() => {
        if (selectedProduct && !products.find(p => p.id === selectedProduct)) {
            getProducts(selectedProduct);
        }
    }, [selectedProduct]);

    useEffect(() => {
        if (changeProducts) {
            changeProducts(products);
        }
    }, [products]);

    const getProducts = async (term) => {
        abortRef.current?.abort();

        const controller = new AbortController();
        abortRef.current = controller;
        setLoading?.(true);

        let url = `/product/search`;

        let params = {
            page,
            per_page: perPage,
        };

        if (onlyQuotable) params.quotable = 1;

        if(similarProduct){
            params.similar_product_id = similarProduct.id
        } else {
            params.term = term;
        }
        if (filterOptions?.orientation !== null && filterOptions?.orientation !== undefined) {
            params.orientation = filterOptions.orientation;
        }

        // Filtros do trilho lateral (fornecedor, preço, cor, estoque).
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    params[key] = value;
                }
            });
        }

        api.get(url, {
            signal: controller.signal,
            params
        }).then(res => {
            setProducts(res.data.resources);
        }).catch(error => {
            if (error.code !== undefined) {
                renderError(error);
            }
        }).finally(() => {
            // Só a requisição corrente desliga o loading. O abort acima rejeita
            // a anterior, e o `finally` dela roda em microtask logo DEPOIS do
            // setLoading(true) desta — desligando o loading com a busca nova
            // ainda em voo. Como a lista já estava vazia, a tela mostrava
            // "Nenhum produto encontrado" durante toda a requisição.
            if (abortRef.current === controller) {
                setLoading?.(false);
            }
        });
    };

    return (
        <>
            {!similarProduct &&
                <div className="search-product-wrapper">
                        <input
                            type="text"
                            ref={inputRef}
                            className="search-product-input"
                            placeholder="Buscar produto..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <Icon name="search" size={16} className="bi-search" />
                </div>
            }
            {similarProduct &&
                <div className="similar-product-wrapper">
                    <img src={similarProduct.image?.image_url?.thumbnail} alt={similarProduct.name} />
                    <div className="details">
                        <p>Buscando parecidos com:</p>
                        <p className="product-name">{similarProduct.sku} - {similarProduct.name}</p>
                    </div>
                    <button onClick={() => setSimilarProduct(null)}>
                        <Icon name="close" />
                    </button>
                </div>
            }
        </>
    );
});

export default SearchProducts;
